import React, { useState, useEffect, useCallback } from 'react';
import { DealsApi } from 'api/api';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { PageHeader } from 'components/page-headers/page-headers';
import { Main } from 'container/styled';
import { Card, Row, Col, Tag, Button, Space, Tabs, Progress, Spin } from 'antd';
import FeatherIcon from 'feather-icons-react';
import styled from 'styled-components';
import { formatCurrency, formatDateTime, formatSquareMeters } from 'utility/utility';
import { useTranslation } from 'react-i18next';
import RecordPaymentModal from './components/Modals/RecordPaymentModal';
import UpdateStageModal from './components/Modals/UpdateStageModal';
import DealTimeline from './components/DealTimeline';
import AddNoteDrawer from './components/Modals/AddNoteDrawer';
import PaymentSchedule from './components/PaymentSchedule';
import {
  CashFlowStatus,
  DealActivityDto,
  DealNoteDto,
  DealStage,
  PaymentScheduleDto,
  UnitAttachmentDto,
  DealUnitFloorDto,
  DealBasicDetailsDto,
  DealUnitDto,
  DealPaymentsDto,
  DealTimelineDto,
  DealAttachmentDto,
  ClientProjectPlansDto,
} from 'api/models';
import { DealStageConfig, getStageColor } from './constants';
import dayjs from 'dayjs';
import PaymentPlanModal from './components/Modals/PaymentPlanModal';
import DealAttachments from './components/DealAttachments';
import ProjectPlansTable from '../projects/components/ProjectPlansTable';
import { getProjectPlanStatusName } from 'pages/projects/constants';
import { getPriorityName } from 'pages/projects/constants';
import DealSummaryPDF from './components/DealSummaryPDF';
import ReactDOM from 'react-dom/client';
import { ImageGalleryModal } from 'components/ImageGalleryModal';

const dealsApi = new DealsApi();

// Styled components remain the same...
const DealCard = styled(Card)`
  .ant-card-head {
    min-height: unset;
    padding: 12px 16px;
    border-bottom: none;
  }

  .ant-card-body {
    padding: 16px;
    padding-top: 0px !important;
  }
`;

const StatCard = styled(Card)`
  text-align: center;
  background: ${props => props.color}10;
  border: 1px solid ${props => props.color}30;

  .stat-value {
    font-size: 24px;
    font-weight: 600;
    color: ${props => props.color};
    margin-bottom: 4px;
  }

  .stat-label {
    color: rgba(0, 0, 0, 0.45);
    font-size: 14px;
  }
`;

const DetailItem = styled.div`
  margin-bottom: 16px;

  .label {
    color: rgba(0, 0, 0, 0.45);
    font-size: 13px;
    margin-bottom: 4px;
  }

  .value {
    font-size: 15px;
  }
`;

const UnitsList = styled.div`
  .unit-item {
    padding: 16px;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
      border-bottom: none;
    }
  }
`;

const TimelineCard = styled(DealCard)`
  .ant-card-body {
    max-height: 308px !important;
    overflow-y: auto;
  }
`;

const EmptyPaymentState = styled.div`
  text-align: center;
  padding: 32px;
  background: #f8f9fa;
  border-radius: 6px;
  margin-bottom: 24px;

  .icon {
    font-size: 48px;
    color: #bfbfbf;
    margin-bottom: 16px;
  }

  .title {
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 8px;
  }

  .description {
    color: rgba(0, 0, 0, 0.45);
    margin-bottom: 16px;
  }
`;

const ActionButton = styled(Button)`
  padding: 0 4px;
  height: auto;
`;

interface PaymentModalState {
  visible: boolean;
  paymentId: number | null;
}

interface LoadingStates {
  basic: boolean;
  units: boolean;
  payments: boolean;
  timeline: boolean;
  attachments: boolean;
  projectPlans: boolean;
}

const DealDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const pdfGenerator = DealSummaryPDF();
  const { t } = useTranslation();

  // Split state for different data sections
  const [loadingStates, setLoadingStates] = useState<LoadingStates>({
    basic: true,
    units: false,
    payments: true, // Start loading payments by default
    timeline: false,
    attachments: false,
    projectPlans: false,
  });

  const [deal, setDeal] = useState<DealBasicDetailsDto>();
  const [units, setUnits] = useState<DealUnitDto[]>([]);
  const [payments, setPayments] = useState<DealPaymentsDto>();
  const [timeline, setTimeline] = useState<DealTimelineDto>();
  const [attachments, setAttachments] = useState<DealAttachmentDto[]>([]);
  const [projectPlans, setProjectPlans] = useState<ClientProjectPlansDto[]>([]);

  const [paymentModal, setPaymentModal] = useState<PaymentModalState>({ visible: false, paymentId: null });
  const [stageModal, setStageModal] = useState(false);
  const [noteDrawer, setNoteDrawer] = useState(false);
  const [paymentPlanModal, setPaymentPlanModal] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [galleryUnit, setGalleryUnit] = useState<{ unitId: number; attachments: UnitAttachmentDto[] } | null>(null);
  const [activeTab, setActiveTab] = useState(() => {
    return window.location.hash.replace('#', '') || 'units';
  });

  // Helper function to update loading state
  const updateLoadingState = useCallback((section: keyof LoadingStates, loading: boolean) => {
    setLoadingStates(prev => ({ ...prev, [section]: loading }));
  }, []);

  // Fetch basic deal details (always loads first)
  const fetchBasicDetails = useCallback(async () => {
    try {
      updateLoadingState('basic', true);
      const response = await dealsApi.apiDealsIdGet({ id: Number(id) });
      setDeal(response.data);
    } catch (error) {
      console.error('Error fetching basic deal details:', error);
    } finally {
      updateLoadingState('basic', false);
    }
  }, [id, updateLoadingState]);

  // Fetch units
  const fetchUnits = useCallback(async () => {
    if (!deal || units.length > 0) return; // Don't fetch if already loaded

    try {
      updateLoadingState('units', true);
      const response = await dealsApi.apiDealsIdUnitsGet({ id: Number(id) });
      setUnits(response.data);
    } catch (error) {
      console.error('Error fetching deal units:', error);
    } finally {
      updateLoadingState('units', false);
    }
  }, [id, deal, units.length, updateLoadingState]);

  // Fetch payments - Modified to not check if payments already exist
  const fetchPayments = useCallback(async () => {
    if (!deal) return; // Only check if deal exists

    try {
      updateLoadingState('payments', true);
      const response = await dealsApi.apiDealsIdPaymentsGet({ id: Number(id) });
      setPayments(response.data);
    } catch (error) {
      console.error('Error fetching deal payments:', error);
    } finally {
      updateLoadingState('payments', false);
    }
  }, [id, deal, updateLoadingState]);

  // Fetch timeline
  const fetchTimeline = useCallback(async () => {
    if (!deal || timeline) return; // Don't fetch if already loaded

    try {
      updateLoadingState('timeline', true);
      const response = await dealsApi.apiDealsIdTimelineGet({ id: Number(id) });
      setTimeline(response.data);
    } catch (error) {
      console.error('Error fetching deal timeline:', error);
    } finally {
      updateLoadingState('timeline', false);
    }
  }, [id, deal, timeline, updateLoadingState]);

  // Fetch attachments
  const fetchAttachments = useCallback(async () => {
    if (!deal || attachments.length > 0) return; // Don't fetch if already loaded

    try {
      updateLoadingState('attachments', true);
      const response = await dealsApi.apiDealsIdAttachmentsGet({ id: Number(id) });
      setAttachments(response.data);
    } catch (error) {
      console.error('Error fetching deal attachments:', error);
    } finally {
      updateLoadingState('attachments', false);
    }
  }, [id, deal, attachments.length, updateLoadingState]);

  // Fetch project plans
  const fetchProjectPlans = useCallback(async () => {
    if (!deal || projectPlans.length > 0) return; // Don't fetch if already loaded

    try {
      updateLoadingState('projectPlans', true);
      const response = await dealsApi.apiDealsIdProjectPlansGet({ id: Number(id) });
      setProjectPlans(response.data);
    } catch (error) {
      console.error('Error fetching project plans:', error);
    } finally {
      updateLoadingState('projectPlans', false);
    }
  }, [id, deal, projectPlans.length, updateLoadingState]);

  // Load basic details on mount
  useEffect(() => {
    fetchBasicDetails();
  }, [fetchBasicDetails]);

  // Load payments and timeline immediately after basic details (for header info and summary cards)
  useEffect(() => {
    if (deal) {
      fetchPayments(); // Load payments by default
      fetchTimeline();
    }
  }, [deal, fetchPayments, fetchTimeline]);

  // Load data based on active tab (excluding payments since it's already loaded)
  useEffect(() => {
    if (!deal) return;

    switch (activeTab) {
      case 'units':
        fetchUnits();
        break;
      case 'payments':
        // Payments are already loaded by default, no need to fetch again
        break;
      case 'attachments':
        fetchAttachments();
        break;
      case 'project-plans':
        fetchProjectPlans();
        break;
    }
  }, [activeTab, deal, fetchUnits, fetchAttachments, fetchProjectPlans]);

  // Refresh functions for modals/actions
  const refreshBasicDetails = fetchBasicDetails;
  const refreshPayments = useCallback(async () => {
    setPayments(undefined); // Clear cache
    await fetchPayments();
  }, [fetchPayments]);

  const refreshTimeline = useCallback(async () => {
    setTimeline(undefined); // Clear cache
    await fetchTimeline();
  }, [fetchTimeline]);

  const refreshAttachments = useCallback(async () => {
    setAttachments([]); // Clear cache
    await fetchAttachments();
  }, [fetchAttachments]);

  const onStageUpdate = async () => {
    await refreshBasicDetails();
    setStageModal(false);
  };

  const onPaymentRecord = async () => {
    await refreshPayments();
    setPaymentModal({ visible: false, paymentId: null });
  };

  const onNoteAdd = async () => {
    await refreshTimeline();
    setNoteDrawer(false);
  };

  const calculateMaxAmount = (deal: DealBasicDetailsDto | undefined, payments: DealPaymentsDto | undefined): number => {
    if (!deal || !payments?.schedule?.length) {
      return payments?.totalAmount || deal?.totalAmount || 0;
    }

    // Calculate the sum of paid cash flows (paidDate is present)
    const sumOfPaidCashFlows = payments.schedule
      .filter(installment => installment.paidDate)
      .reduce((sum, installment) => sum + (installment.paidAmount || 0), 0);

    // Calculate the sum of unpaid cash flows (paidDate is not present)
    const sumOfUnpaidCashFlows = payments.schedule
      .filter(installment => !installment.paidDate)
      .reduce((sum, installment) => sum + (installment.expectedAmount || 0), 0);

    // Max amount is total deal amount minus sums of paid and unpaid cash flows
    const maxAmount = Math.max(0, (payments.totalAmount || 0) - sumOfPaidCashFlows - sumOfUnpaidCashFlows);

    return maxAmount;
  };

  const canAddMoreCashflows =
    deal?.isActive &&
    // Condition 1: All existing payments are cancelled
    (payments?.schedule?.every(installment => installment.status === CashFlowStatus.Cancelled) ||
      calculateMaxAmount(deal, payments) > 0);

  const handleAddPayment = () => {
    setPaymentPlanModal(true);
  };

  const onPaymentPlanSuccess = async () => {
    await refreshPayments();
    setPaymentPlanModal(false);
  };

  const handleDownloadSummary = async () => {
    if (!deal) return;
    setPdfLoading(true);
    try {
      // For PDF generation, we might need to fetch all data
      // You could create a separate endpoint that returns everything for PDF
      const fullDealData = {
        ...deal,
        units,
        payments,
        timeline,
        attachments,
        clientProjectPlans: projectPlans
      };

      await pdfGenerator.generatePDF({
        deal: {
          basic: deal,
          units,
          payments,
          clientProjectPlans: projectPlans
        },
        t: t,
      });
    } finally {
      setPdfLoading(false);
    }
  };

  // Update URL hash when tab changes
  const handleTabChange = (key: string) => {
    setActiveTab(key);
    window.location.hash = key;
  };

  return (
    <>
      <PageHeader
        ghost
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span>{`${t('deals:deal-with', 'Deal with')} ${deal?.clientName || ''}`}</span>
            {payments?.nextPaymentDue && (
              <>
                <span style={{ color: '#d9d9d9' }}>|</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#1890ff' }}>
                  <FeatherIcon icon="calendar" size={16} />
                  {t('deals:next-payment', 'Next Payment')}:
                  <strong>{dayjs(payments.nextPaymentDue.dueDate).format('YYYY-MM-DD')}</strong>
                  <span>({formatCurrency(payments.nextPaymentDue.amount)})</span>
                </span>
              </>
            )}
          </div>
        }
        buttons={[
          <Button type="primary" onClick={() => setStageModal(true)} icon={<FeatherIcon icon="edit-3" size={16} />}>
            {t('deals:update-stage', 'Update Stage')}
          </Button>,
          deal?.isActive && !deal?.closedAt && (!payments?.paidAmount || payments?.paidAmount === 0) && (
            <Button onClick={() => navigate(`/deals/${id}/edit`)} icon={<FeatherIcon icon="edit-2" size={16} />}>
              {t('global:edit', 'Edit')}
            </Button>
          ),
          <Button
            onClick={() => handleDownloadSummary()}
            icon={<FeatherIcon icon="file-text" size={16} />}
            loading={pdfLoading}
          >
            {t('deals:download-summary', 'Download Summary')}
          </Button>,
        ]}
      />

      <Main>
        <Row gutter={[24, 24]}>
          {/* Basic Info & Stats */}
          <Col span={17}>
            <DealCard
              loading={loadingStates.basic}
              title={
                <Space>
                  <FeatherIcon icon="info" size={16} />
                  {t('deals:details', 'Details')}
                </Space>
              }
            >
              {/* Details Section */}
              <div
                style={{
                  marginBottom: '16px',
                  paddingTop: '0px !important',
                  paddingBottom: '16px',
                  borderBottom: '1px solid #f0f0f0',
                }}
              >
                <Row gutter={24}>
                  {/* Stage and Created Date */}
                  <Col span={12}>
                    <DetailItem>
                      <div className="label">{t('deals:stage', 'Stage')}</div>
                      <Tag color={getStageColor(deal?.stage as DealStage)}>
                        {t(
                          DealStageConfig[deal?.stage as DealStage]?.key || 'dealStage.unknown',
                          DealStageConfig[deal?.stage as DealStage]?.title || 'Unknown',
                        )}
                      </Tag>
                    </DetailItem>
                  </Col>
                  <Col span={12}>
                    <DetailItem>
                      <div className="label">{t('deals:created-at', 'Created At')}</div>
                      <div className="value">{formatDateTime(deal?.createdAt)}</div>
                    </DetailItem>
                  </Col>
                </Row>
                <Row gutter={24}>
                  {/* Client and Project */}
                  <Col span={12}>
                    <DetailItem>
                      <div className="label">{t('deals:client-name', 'Client')}</div>
                      <Link to={`/clients/${deal?.clientId}`}>{deal?.clientName}</Link>
                    </DetailItem>
                  </Col>
                  <Col span={12}>
                    <DetailItem>
                      <div className="label">{t('deals:project-name', 'Project')}</div>
                      <Link to={`/projects/${deal?.projectId}`}>{deal?.projectName}</Link>
                    </DetailItem>
                  </Col>
                </Row>
              </div>

              {/* Totals Section - Always show loading or data */}
              {loadingStates.payments && !payments ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Spin size="small" />
                  <div style={{ marginTop: '8px', color: 'rgba(0, 0, 0, 0.45)' }}>
                    {t('deals:loading-payments', 'Loading payment information...')}
                  </div>
                </div>
              ) : payments ? (
                <>
                  <Row gutter={16} style={{ marginBottom: '12px' }}>
                    <Col span={8}>
                      <StatCard color="#1890ff" style={{ padding: '12px', fontSize: '12px' }}>
                        <div
                          className="stat-value"
                          style={{
                            fontSize: '18px',
                            fontWeight: 500,
                            lineHeight: '1.2',
                            marginBottom: '4px',
                          }}
                        >
                          {formatCurrency(payments?.totalAmount)}
                        </div>
                        <div
                          className="stat-label"
                          style={{
                            fontSize: '12px',
                            color: 'rgba(0, 0, 0, 0.6)',
                          }}
                        >
                          {t('deals:total-amount', 'Total Amount')}
                        </div>
                      </StatCard>
                    </Col>
                    <Col span={8}>
                      <StatCard color="#52c41a" style={{ padding: '12px', fontSize: '12px' }}>
                        <div
                          className="stat-value"
                          style={{
                            fontSize: '18px',
                            fontWeight: 500,
                            lineHeight: '1.2',
                            marginBottom: '4px',
                          }}
                        >
                          {formatCurrency(payments?.paidAmount)}
                        </div>
                        <div
                          className="stat-label"
                          style={{
                            fontSize: '12px',
                            color: 'rgba(0, 0, 0, 0.6)',
                          }}
                        >
                          {t('deals:amount-paid', 'Amount Paid')}
                        </div>
                      </StatCard>
                    </Col>
                    <Col span={8}>
                      <StatCard color="#faad14" style={{ padding: '12px', fontSize: '12px' }}>
                        <div
                          className="stat-value"
                          style={{
                            fontSize: '18px',
                            fontWeight: 500,
                            lineHeight: '1.2',
                            marginBottom: '4px',
                          }}
                        >
                          {formatCurrency(payments?.pendingAmount)}
                        </div>
                        <div
                          className="stat-label"
                          style={{
                            fontSize: '12px',
                            color: 'rgba(0, 0, 0, 0.6)',
                          }}
                        >
                          {t('deals:amount-pending', 'Amount Pending')}
                        </div>
                      </StatCard>
                    </Col>
                  </Row>
                  {/* Progress & Next Payment */}
                  <div className="mt-6">
                    <Progress
                      percent={Math.round((payments?.paidAmount! / payments?.totalAmount!) * 100)}
                      status={payments?.paidAmount === payments?.totalAmount ? 'success' : 'active'}
                    />
                  </div>
                </>
              ) : (
                // This fallback should rarely be seen now since payments load by default
                <div style={{ textAlign: 'center', padding: '20px', color: 'rgba(0, 0, 0, 0.45)' }}>
                  {t('deals:no-payment-data', 'No payment information available')}
                </div>
              )}
            </DealCard>
          </Col>
          {/* Deal Summary & Timeline */}
          <Col span={7}>
            <TimelineCard
              loading={loadingStates.timeline}
              title={
                <Space>
                  <FeatherIcon icon="activity" size={16} />
                  {t('deals:timeline', 'Timeline')}
                </Space>
              }
              extra={
                deal?.isActive && (
                  <Space>
                    <Button
                      onClick={() => setNoteDrawer(true)}
                      icon={<FeatherIcon icon="message-circle" size={16} style={{ marginRight: '4px' }} />}
                    >
                      {t('deals:add-note', 'Add Note')}
                    </Button>
                  </Space>
                )
              }
            >
              {timeline ? (
                <DealTimeline activities={timeline.activities || []} notes={timeline.notes || []} />
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(0, 0, 0, 0.45)' }}>
                  <FeatherIcon icon="activity" size={24} style={{ marginBottom: '8px' }} />
                  <div>{t('deals:timeline-loading', 'Loading timeline...')}</div>
                </div>
              )}
            </TimelineCard>
          </Col>
        </Row>
        <Row gutter={[24, 24]} className="mt-4">
          <Col span={24}>
            {/* Tabs Section */}
            <Card loading={loadingStates.basic}>
              <Tabs activeKey={activeTab} onChange={handleTabChange}>
                {/* Units Tab */}
                <Tabs.TabPane
                  tab={
                    <span>
                      <FeatherIcon icon="box" size={16} />
                      {t('deals:units', 'Units')}
                      {loadingStates.units && <Spin size="small" style={{ marginLeft: '8px' }} />}
                    </span>
                  }
                  key="units"
                >
                  {loadingStates.units ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                      <Spin size="large" />
                      <div style={{ marginTop: '16px', color: 'rgba(0, 0, 0, 0.45)' }}>
                        {t('deals:loading-units', 'Loading units...')}
                      </div>
                    </div>
                  ) : (
                    <UnitsList>
                      {units?.map(unit => (
                        <div key={unit.unitId} className="unit-item">
                          <Row>
                            <Col span={4}>
                              <DetailItem>
                                <div className="label">{t('units:name', 'Unit')}</div>
                                <div className="value">
                                  <Link to={`/units/${unit.unitId}`}>{unit.unitName}</Link>
                                </div>
                              </DetailItem>
                            </Col>
                            <Col span={4}>
                              <DetailItem>
                                <div className="label">{t('units:category', 'Category')}</div>
                                <div className="value">
                                  <Link to={`/unit-categories/${unit.categoryId}`}>{unit.categoryName}</Link>
                                </div>
                              </DetailItem>
                            </Col>
                            <Col span={4}>
                              <DetailItem>
                                <div className="label">{t('units:quantity', 'Quantity')}</div>
                                <div className="value">
                                  {unit.quantity}
                                  {unit.floors && unit.floors.length > 0 && (
                                    <div style={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.45)', marginTop: '4px' }}>
                                      {unit.floors.map((floor: DealUnitFloorDto) => (
                                        <div key={floor.id}>
                                          {floor.description
                                            ? t(
                                              'units:floor-allocations-with-description',
                                              '{{floor}} - {{quantity}} unit(s)',
                                              {
                                                floor: floor.description,
                                                quantity: floor.quantity,
                                              },
                                            )
                                            : t('units:floor-allocations', '{{floor}} - {{quantity}} unit(s)', {
                                              floor: t('categories:floor-number', 'Floor {{number}}', {
                                                number: floor.floorNumber,
                                              }),
                                              quantity: floor.quantity,
                                            })}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </DetailItem>
                            </Col>
                            <Col span={3}>
                              <DetailItem>
                                <div className="label">{t('units:square-meters', 'Square Meters')}</div>
                                <div className="value">{formatSquareMeters(unit.squareMeters!)}</div>
                              </DetailItem>
                            </Col>
                            <Col span={3}>
                              <DetailItem>
                                <div className="label">{t('units:price-per-sqm', 'Price per m²')}</div>
                                <div className="value">{formatCurrency(unit.negotiatedPricePerSqm)}</div>
                              </DetailItem>
                            </Col>
                            <Col span={4}>
                              <DetailItem>
                                <div className="label">{t('units:total-price', 'Total Price')}</div>
                                <div className="value">{formatCurrency(unit.totalPrice)}</div>
                              </DetailItem>
                            </Col>
                            <Col span={2}>
                              <DetailItem>
                                <div className="label">{t('common:actions', 'Actions')}</div>
                                <div className="value">
                                  {unit.attachments && unit.attachments.some(att => att.fileType?.startsWith('image/')) && (
                                    <ActionButton
                                      type="link"
                                      icon={<FeatherIcon icon="image" size={16} style={{ marginRight: '4px' }} />}
                                      onClick={() =>
                                        setGalleryUnit({ unitId: unit.unitId!, attachments: unit.attachments! })
                                      }
                                      title={t('units:view-gallery', 'View Gallery')}
                                    >
                                      {t('units:view-gallery', 'View Gallery')}
                                    </ActionButton>
                                  )}
                                </div>
                              </DetailItem>
                            </Col>
                          </Row>
                          {unit.notes && <div className="mt-2 text-gray-500 text-sm">{unit.notes}</div>}
                        </div>
                      ))}
                    </UnitsList>
                  )}
                </Tabs.TabPane>

                {/* Payments Tab */}
                <Tabs.TabPane
                  tab={
                    <span>
                      <FeatherIcon icon="dollar-sign" size={16} />
                      {t('deals:payments', 'Payments')}
                      {loadingStates.payments && <Spin size="small" style={{ marginLeft: '8px' }} />}
                    </span>
                  }
                  key="payments"
                >
                  {loadingStates.payments ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                      <Spin size="large" />
                      <div style={{ marginTop: '16px', color: 'rgba(0, 0, 0, 0.45)' }}>
                        {t('deals:loading-payments', 'Loading payments...')}
                      </div>
                    </div>
                  ) : (
                    <>
                      {canAddMoreCashflows && (
                        <div style={{ marginBottom: 24 }}>
                          <Button type="primary" icon={<FeatherIcon icon="plus" size={16} />} onClick={handleAddPayment}>
                            {t('deals:schedule-new-payment', 'Schedule New Payment')}
                          </Button>
                        </div>
                      )}

                      {payments?.schedule?.length === 0 ? (
                        <EmptyPaymentState>
                          <div className="icon">
                            <FeatherIcon icon="calendar" size={48} />
                          </div>
                          <div className="title">{t('deals:no-payments', 'No Payments Scheduled')}</div>
                          <div className="description">
                            {t('deals:no-payments-description', 'No payment schedule has been set up for this deal yet.')}
                          </div>
                          <Button type="primary" icon={<FeatherIcon icon="plus" size={16} />} onClick={handleAddPayment}>
                            {t('deals:schedule-payment', 'Schedule Payment')}
                          </Button>
                        </EmptyPaymentState>
                      ) : (
                        <PaymentSchedule
                          payments={payments?.schedule as PaymentScheduleDto[]}
                          onRecordPayment={paymentId => setPaymentModal({ visible: true, paymentId: Number(paymentId) })}
                          disabled={!deal?.isActive}
                          onRefresh={refreshPayments}
                        />
                      )}
                    </>
                  )}
                </Tabs.TabPane>

                {/* Attachments Tab */}
                <Tabs.TabPane
                  tab={
                    <span>
                      <FeatherIcon icon="paperclip" size={16} />
                      {t('deals:attachments', 'Attachments')}
                      {loadingStates.attachments && <Spin size="small" style={{ marginLeft: '8px' }} />}
                    </span>
                  }
                  key="attachments"
                >
                  {loadingStates.attachments ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                      <Spin size="large" />
                      <div style={{ marginTop: '16px', color: 'rgba(0, 0, 0, 0.45)' }}>
                        {t('deals:loading-attachments', 'Loading attachments...')}
                      </div>
                    </div>
                  ) : (
                    <DealAttachments
                      dealId={deal?.id!}
                      attachments={attachments || []}
                      onAttachmentsChange={refreshAttachments}
                    />
                  )}
                </Tabs.TabPane>

                {/* Project Plans Tab */}
                {(projectPlans?.length ?? 0) > 0 && (
                  <Tabs.TabPane
                    tab={
                      <span>
                        <FeatherIcon icon="clipboard" size={16} />
                        {t('deals:related-plans', 'Unit Requirements')}
                        {loadingStates.projectPlans && <Spin size="small" style={{ marginLeft: '8px' }} />}
                      </span>
                    }
                    key="project-plans"
                  >
                    {loadingStates.projectPlans ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" />
                        <div style={{ marginTop: '16px', color: 'rgba(0, 0, 0, 0.45)' }}>
                          {t('deals:loading-project-plans', 'Loading project plans...')}
                        </div>
                      </div>
                    ) : (
                      <ProjectPlansTable
                        projectId={deal?.projectId!}
                        hideClientColumn
                        hideAddButton
                        initialPlans={projectPlans!}
                      />
                    )}
                  </Tabs.TabPane>
                )}
              </Tabs>
            </Card>
          </Col>
        </Row>
      </Main>

      {/* Modals & Drawers */}
      {stageModal && (
        <UpdateStageModal
          visible={stageModal}
          dealId={Number(id)}
          currentStage={deal?.stage as DealStage}
          dealPayments={payments}
          onSuccess={onStageUpdate}
          onCancel={() => setStageModal(false)}
        />
      )}

      {paymentModal.visible && (
        <RecordPaymentModal
          visible={paymentModal.visible}
          dealId={Number(id)}
          paymentId={paymentModal.paymentId!}
          onSuccess={onPaymentRecord}
          onCancel={() => setPaymentModal({ visible: false, paymentId: null })}
        />
      )}

      {noteDrawer && (
        <AddNoteDrawer
          visible={noteDrawer}
          dealId={Number(id)}
          onSuccess={onNoteAdd}
          onClose={() => setNoteDrawer(false)}
        />
      )}

      {paymentPlanModal && (
        <PaymentPlanModal
          visible={paymentPlanModal}
          dealId={Number(id)}
          onSuccess={onPaymentPlanSuccess}
          maxAmount={calculateMaxAmount(deal, payments)}
          onCancel={() => setPaymentPlanModal(false)}
        />
      )}

      {galleryUnit && (
        <ImageGalleryModal
          visible={!!galleryUnit}
          onClose={() => setGalleryUnit(null)}
          attachments={galleryUnit.attachments}
        />
      )}
    </>
  );
};

export default DealDetailsPage;